import { Flex, Text } from '@adobe/react-spectrum';
import { useBreakpoint } from '@react-spectrum/utils';
import React, { useState } from 'react';
import { useErrorBoundary, withErrorBoundary } from 'react-error-boundary';
import { Helmet } from 'react-helmet';
import { useLocation, useSearchParams } from 'react-router-dom';

import {
  ErrorBoundary,
  ErrorStateFallback,
  NotFoundMessage,
  Pagination,
  SortDropdown,
} from '@exchange-frontends/components';
import { useAnalyticsPageLoad } from '@exchange-frontends/custom-hooks';
import { managePage } from '@exchange-frontends/utils';

import useManageCcApps, {
  ITEMS_PER_PAGE,
  sortOptions,
} from '../../../../../actions/myxchngApi/selectors/useManageCcApps';
import { ROUTES } from '../../../../../constants';
import { useStore } from '../../../store';
import CCCardButton from './buttons/CCCardButton';
import Card from './cards/ManageCard';

// Selectors
// These are here so that they are not re-declared on every render
const selectedOrgSelector = (state) => state.selectedOrg;

const AppsList = () => {
  const location = useLocation();
  const { matchedBreakpoints } = useBreakpoint();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedOrg = useStore(selectedOrgSelector);
  const page = Number(searchParams.get('page') ?? '1');
  /** @type {ReturnType<typeof useState<import('@action-types/ccApp').SortType>>} */
  const [sortType, setSortType] = useState('mostrecent');
  const {
    ccApps: apps,
    isLoading,
    totalApps,
    error,
  } = useManageCcApps({ refetchOnMount: true, staleTime: 5000, sortType, page });
  const ccApps = isLoading ? Array(4).fill(null) : apps;
  useAnalyticsPageLoad(managePage, !isLoading, ccApps?.length);
  const { showBoundary } = useErrorBoundary();

  if (error) {
    showBoundary(error);
  }

  const onPageChange = (page) => {
    window.scrollTo(0, 0);
    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.set('page', page);
    setSearchParams(newSearchParams);
  };

  return (
    <>
      <Helmet>
        <title>{`Manage apps for ${selectedOrg?.name ?? ''} | Adobe Exchange`}</title>
        <meta name="robots" content="noodp, FOLLOW, INDEX" />
        <meta name="title" content={`Manage apps for ${selectedOrg?.name ?? ''} | Adobe Exchange`} />
        <meta property="og:title" content="" />
        <meta name="description" content="" />
        <meta property="og:description" content="" />
        <meta property="og:type" content="website" />
        <meta name="keywords" content="" />
      </Helmet>
      {(totalApps > 0 || isLoading) && (
        <Flex width="100%" justifyContent="space-between" alignItems="center" marginBottom="size-200">
          <Text data-launchid="total-results">
            {isLoading ? (
              `Loading plugins`
            ) : (
              <>
                {totalApps} {'plugin'}
                {totalApps > 1 ? 's' : ''}
              </>
            )}
          </Text>
          <SortDropdown
            defaultSelectedKey={sortType}
            onSelectionChange={setSortType}
            customItems={sortOptions}
            id="manage-sort"
          />
        </Flex>
      )}
      <Flex alignItems="center" direction="column">
        {/*render cc plugins*/}
        {ccApps?.map((app, idx) => (
          <ErrorBoundary
            key={app?.id ?? idx}
            errorMessage={`We've encountered an error trying to display the app with id ${app?.id ?? 'undefined'}`}
          >
            {() => (
              <Card
                data-testid={`app-card-${app?.id ?? idx}`}
                loading={isLoading}
                key={app?.id ?? app?.appNumber ?? idx}
                app={app}
                roundedBottom={ccApps.length === 1 || idx == ccApps.length - 1}
                roundedTop={idx === 0}
                customButton={() => <CCCardButton app={app} loading={isLoading} data-launchid="Manage button" />}
                titleLink={`/apps/cc/${app?.id ?? app?.appNumber}`}
              />
            )}
          </ErrorBoundary>
        ))}
        {totalApps > ITEMS_PER_PAGE && !isLoading && (
          <Pagination
            enablePageNumbers={matchedBreakpoints.includes('S')}
            defaultPage={page}
            totalPages={Math.ceil(totalApps / ITEMS_PER_PAGE)}
            onChange={onPageChange}
          />
        )}
        {totalApps === 0 && (
          <NotFoundMessage
            headingProps={{ maxWidth: '600px' }}
            heading={['No Creative Cloud plugins to manage.', 'Looking for your acquired Creative Cloud plugin?']}
            contentProps={{ marginTop: 'size-150' }}
            content="You might have acquired your plugin using a different organization. Try signing out and then sign back in with the organization you used to acquire the plugin."
            link={{
              text: 'Explore thousands of Creative Cloud plugins',
              href: ROUTES.BROWSE + '/cc',
            }}
            linkProps={{ marginTop: 'size-150' }}
          />
        )}
      </Flex>
    </>
  );
};

export default withErrorBoundary(AppsList, {
  fallbackRender: () => (
    <ErrorStateFallback justifyContent="center" marginTop="size-600" sectionName="Creative Cloud plugins" />
  ),
  onError: console.error,
});
